<template>
    <div class="currency-input">
        <v-text-field
            ref="textField"
            type="text"
            inputmode="decimal"
            :rules="rules"
            v-bind:class="['currency-input__field', showClass]"
            :color="color"
            :label="label"
            v-model="displayValue"
            @blur="on_blur"
            @focus="on_focus"
            :readonly="readonly"
            :disabled="Number(disabled) === 1 || disabled === ''"
            @keydown.enter.prevent="press_enter"
            @keydown.tab="on_tab"
            @keydown.esc="on_esc"
        />
    </div>
</template>
<script>
export default {
    name: 'h-currency-input',
    props: ["value", "label", "disabled", "decimal", "showClass", "readonly", "color", "rules"],
    data: function() {
        return {
            isInputActive: false,
            isModified: false,
            isEnterPressed: false,
            currentValue: ''
        }
    },
    watch: {
        value: {
            handler: function(newVal) {
                if (!this.isInputActive) {
                    this.currentValue = this.getRawValue(newVal);
                }
            },
            immediate: true
        }
    },
    computed: {
        displayValue: {
            get: function() {
                if (this.isInputActive) {
                    // Cursor is inside the input field. unformat display value for user
                    return this.currentValue;
                } else {
                    // User is not modifying now. Format display value for user interface
                    var parseValue = Number(this.value);
                    return this.value ? parseValue.toFixed(this.decimal !== undefined ? this.decimal : 2).replace(/(\d)(?=(\d{3})+(?:\.\d+)?$)/g, "$1,") : '0';
                }
            },
            set: function(modifiedValue) {
                this.currentValue = modifiedValue;
                this.isModified = true;
            }
        }
    },
    methods: {
        getRawValue(val) {
            if (val === null || val === undefined || val === '') {
                return '';
            }
            return String(val);
        },
        parseCurrencyValue(value) {
            const sanitizedValue = String(value == null ? '' : value).replace(/[^\d.,]/g, '')
            const commaIndex = sanitizedValue.lastIndexOf(',')
            const dotIndex = sanitizedValue.lastIndexOf('.')

            if (commaIndex === -1 && dotIndex === -1) {
                return parseFloat(sanitizedValue)
            }

            let decimalSeparator = null
            if (commaIndex !== -1 && dotIndex !== -1) {
                // When both separators exist, the rightmost one is the decimal separator.
                decimalSeparator = commaIndex > dotIndex ? ',' : '.'
            } else {
                const separator = commaIndex !== -1 ? ',' : '.'
                const groups = sanitizedValue.split(separator)
                const isThousandsFormat = groups.length > 1 && groups.slice(1).every(group => group.length === 3)

                // A single separator followed by three digits is normally a thousands separator.
                // Keep values such as 0.125 as decimals because the integer part is zero.
                if (!isThousandsFormat || (groups.length === 2 && Number(groups[0]) === 0)) {
                    decimalSeparator = separator
                }
            }

            if (!decimalSeparator) {
                return parseFloat(sanitizedValue.replace(/[.,]/g, ''))
            }

            const decimalIndex = sanitizedValue.lastIndexOf(decimalSeparator)
            const integerPart = sanitizedValue.slice(0, decimalIndex).replace(/[.,]/g, '') || '0'
            const fractionPart = sanitizedValue.slice(decimalIndex + 1).replace(/[.,]/g, '')
            return parseFloat(`${integerPart}.${fractionPart}`)
        },
        commitValue() {
            if (!this.isModified) {
                return;
            }
            this.isModified = false;
            let newValue = this.parseCurrencyValue(this.currentValue);
            // Ensure that it is not NaN
            if (isNaN(newValue)) {
                newValue = 0;
            }
            const currentValueNum = (this.value !== null && this.value !== undefined && this.value !== '') ? Number(this.value) : null;
            if (currentValueNum === null || newValue !== currentValueNum) {
                this.$emit('input', newValue);
                this.$emit('change', newValue);
            }
        },
        on_focus(event) {
            this.isInputActive = true;
            this.isModified = false;
            this.currentValue = this.getRawValue(this.value);
            this.$emit('focus', event);
        },
        press_enter(event) {
            this.isEnterPressed = true;
            this.commitValue();
            this.isInputActive = false;
            if (this.$refs.textField && typeof this.$refs.textField.blur === 'function') {
                this.$refs.textField.blur();
            } else if (event && event.target && typeof event.target.blur === 'function') {
                event.target.blur();
            }
            this.$emit('press_enter', event);
            setTimeout(() => {
                this.isEnterPressed = false;
            }, 100);
        },
        on_tab(event) {
            this.commitValue();
            this.isInputActive = false;
        },
        on_esc(event) {
            this.isModified = false;
            this.isInputActive = false;
            this.currentValue = this.getRawValue(this.value);
            if (this.$refs.textField && typeof this.$refs.textField.blur === 'function') {
                this.$refs.textField.blur();
            } else if (event && event.target && typeof event.target.blur === 'function') {
                event.target.blur();
            }
        },
        on_blur(event) {
            if (this.isEnterPressed) {
                this.isEnterPressed = false;
                return;
            }
            this.commitValue();
            this.isInputActive = false;
            this.$emit('on_blur', event);
            this.$emit('blur', event);
        }
    }
}
</script>
<style scoped>
.currency-input {
    width: 100%;
}

.currency-input__field {
    width: 100%;
}
</style>
